/**
 * Typed client for the marketplace (SPEC-13 / SPEC-16).
 *
 * NO ARITHMETIC LIVES HERE. Not a commission, not a floor, not a rate. Every
 * figure this module carries was computed by a SQL function in migration 0189
 * and handed over by the gateway. The screens format cents into a string and
 * do nothing else to them — the moment a percentage is computed in the browser
 * there are two implementations of the corridor and exactly one of them gets
 * edited the day the founder moves it.
 *
 * `founderPlaceholder` travels on every envelope and every screen has to show
 * it. These are not agreed numbers.
 *
 * `payment.available` is false on every response and will stay false until a
 * processor exists. The checkout screen renders it as a sentence, not as a
 * disabled button with no explanation.
 */
import { ApiError, apiJson } from './client';

export interface MarketplacePaymentState {
  available: boolean;
  reason: string;
  message: string;
}

/**
 * Audience-shaped runtime truth from the gateway.
 *
 * These are deliberately not reconstructed from `VITE_*`. Build flags decide
 * whether the Market bundle is reachable at all; every child surface follows
 * this DTO so a dark server stage cannot be probed by a direct URL.
 */
export interface MarketplaceCapabilities {
  talent: boolean;
  talentEngagements: boolean;
  talentEngagementAcceptance: boolean;
  talentPayments: boolean;
  talentReviews: boolean;
  talentModeration: boolean;
  store: boolean;
  storePublishing: boolean;
  storePublication: boolean;
  storeOfficial: boolean;
  storeLibrary: boolean;
  storeRuntimeAttach: boolean;
  storeTrials: boolean;
  storePayments: boolean;
  storePaidAcquisition: boolean;
  storeReviews: boolean;
  storeModeration: boolean;
  storeModerationActions: boolean;
  storeAutoUpdate: boolean;
  sellerProfile: boolean;
  moderation: boolean;
}

export interface MarketplaceCapabilitiesResponse {
  capabilities: MarketplaceCapabilities;
}

export interface MarketplacePrice {
  id: string;
  type: string | null;
  amountCents: number | null;
  currency: string | null;
}

export interface MarketplacePackPublisher {
  handle: string | null;
  displayName: string | null;
  country: string | null;
}

export interface MarketplacePack {
  id: string;
  slug: string;
  title: string;
  summary: string;
  descriptionMd: string;
  category: string;
  roleDirection: string;
  tags: string[];
  channel: string;
  status: string;
  rating: number | null;
  ratingCount: number;
  installCount: number;
  verifiedSales: number;
  publishedAt: string | null;
  publisher: MarketplacePackPublisher;
  version: {
    id: string;
    semver: string | null;
    /**
     * The sha256 the DATABASE computed over this version's contents (0264).
     * `null` is not "empty" — it means the version has no contents at all,
     * which is a pack the gateway refuses to publish.
     */
    fingerprint: string | null;
    itemCount: number;
  } | null;
  price: MarketplacePrice | null;
  purchasable: boolean;
}

/** One skill inside a pack. The BODY never crosses the wire — it is the product. */
export interface MarketplacePackItem {
  ordinal: number;
  slug: string;
  displayName: string;
  description: string | null;
  contentSha256: string;
  sourceSkillId?: string | null;
  sourceOrgId?: string | null;
}

export interface MarketplacePackContents {
  versionId?: string;
  fingerprint: string | null;
  itemCount?: number;
  items: MarketplacePackItem[];
}

/** A skill this Workspace has, offered to the pack builder. */
export interface MarketplaceWorkspaceSkill {
  id: string;
  slug: string;
  displayName: string;
  description: string | null;
  category: string | null;
  enabled: boolean;
}

export interface MarketplaceQuote {
  scope?: string;
  grossCents: number;
  commissionBps: number;
  commissionCents: number;
  sellerNetCents: number;
  currency: string;
  policyFingerprint?: string;
}

export interface MarketplaceSellerGeo {
  verdict: string;
  paid_sales_allowed: boolean;
  free_publishing_allowed: boolean;
  payout_provider?: string | null;
  reason?: string | null;
}

export interface MarketplaceReview {
  id: string;
  rating: number;
  bodyMd: string;
  verifiedPurchase: boolean;
  createdAt: string;
}

export interface MarketplaceExpert {
  id: string;
  handle: string;
  displayName: string;
  headline: string | null;
  bioMd: string | null;
  skillTags: string[];
  languages: string[];
  country: string | null;
  timezone: string | null;
  status: string;
  rateHint: { amountCents: number; currency: string; unit: string | null } | null;
  verificationTier: string;
  rating: number | null;
  ratingCount: number;
  engagementsCompleted: number;
  publishedAt: string | null;
}

export interface MarketplacePolicyRow {
  scope: string;
  key: string;
  kind: string;
  num: string | null;
  bool: boolean | null;
  text: string | null;
  list: string[] | null;
  origin: string;
  placeholder: boolean;
  canonRef: string | null;
}

export interface MarketplacePolicyResponse {
  founderPlaceholder: boolean;
  placeholderCount: number;
  note: string;
  payment: MarketplacePaymentState;
  currency: string;
  priceFloorCents: number | null;
  priceSoftCapCents: number | null;
  quote: MarketplaceQuote;
  talentQuote?: MarketplaceQuote | null;
  priceVerdict: string | null;
  sellerGeo: MarketplaceSellerGeo | null;
  rows: MarketplacePolicyRow[];
  capabilities?: MarketplaceCapabilities;
}

export interface MarketplacePacksResponse {
  founderPlaceholder: boolean;
  note: string;
  payment: MarketplacePaymentState;
  packs: MarketplacePack[];
  count: number;
}

export type MarketplaceExpertSummary = Pick<
  MarketplaceExpert,
  | 'id'
  | 'handle'
  | 'displayName'
  | 'headline'
  | 'skillTags'
  | 'languages'
  | 'country'
  | 'timezone'
  | 'rateHint'
  | 'verificationTier'
  | 'rating'
  | 'ratingCount'
  | 'engagementsCompleted'
  | 'publishedAt'
>;

export interface MarketplaceExpertsResponse {
  experts: MarketplaceExpertSummary[];
  page: number;
  limit: number;
  hasMore: boolean;
  payment: MarketplacePaymentState;
}

/**
 * What THIS viewer may do with the thing they are looking at.
 *
 * `reviewReason` is a CODE, turned into a sentence by the screen.
 * 'own_subject' and 'no_relationship' are different facts — the first never
 * changes, the second changes the moment they order or hire — and a screen that
 * printed one sentence for both would be lying to half of the people reading it.
 */
export interface MarketplaceViewer {
  isOwner: boolean;
  canReview: boolean;
  reviewReason:
    | 'ok'
    | 'own_subject'
    | 'no_relationship'
    | 'not_paid_terminal'
    | 'no_owned_account'
    | 'feature_disabled';
  /** True only after the server verifies paid, non-refunded processor evidence. */
  reviewWouldBeVerified: boolean;
  review: MarketplaceReview | null;
}

export interface MarketplacePackResponse {
  founderPlaceholder: boolean;
  note: string;
  payment: MarketplacePaymentState;
  pack: MarketplacePack;
  viewer: MarketplaceViewer;
  priceVerdict: string;
  quote: MarketplaceQuote | null;
  /** What is in the pack, as the buyer is allowed to see it: names + digests. */
  contents: MarketplacePackContents | null;
  reviews: MarketplaceReview[];
}

export interface MarketplacePackVersion {
  id: string;
  semver: string;
  versionNo: number;
  status: string;
  changelogMd: string;
  publishedAt: string | null;
  createdAt: string;
}

export interface MarketplacePackPrice {
  id: string;
  type: string;
  amountCents: number;
  currency: string;
  active: boolean;
  createdAt: string;
}

/** The editor's read: the pack plus the history the storefront never shows. */
export interface MarketplaceSellerPackResponse {
  founderPlaceholder: boolean;
  note: string;
  payment: MarketplacePaymentState;
  pack: MarketplacePack;
  priceVerdict: string;
  quote: MarketplaceQuote | null;
  contents: MarketplacePackContents | null;
  maxItems: number;
  versions: MarketplacePackVersion[];
  prices: MarketplacePackPrice[];
}

export interface MarketplaceEngagement {
  id: string;
  status: string;
  title: string;
  scope_md: string;
  currency: string;
  gross_cents: number;
  commission_bps_applied: number;
  commission_cents: number;
  expert_net_cents: number;
  policy_fingerprint: string;
  payout_hold_days_applied: number;
  expert_geo_verdict_applied?: string | null;
  started_at: string | null;
  delivered_at: string | null;
  accepted_at: string | null;
  transfer_eligible_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  expert_handle?: string | null;
  expert_display_name?: string | null;
}

export type MarketplaceEngagementCapability = 'read' | 'comment' | 'write';

/**
 * The exact proposal accepted while the M2 engagement rail is dark.
 *
 * There is intentionally no free-form `scopeMd` field here. The gateway
 * normalizes these source fields and serializes the canonical contract into
 * the legacy database column. Keeping the source shape typed prevents the UI
 * from regressing to an attractive four-field form that the real route must
 * refuse.
 */
export interface MarketplaceDarkEngagementProposal {
  expertHandle: string;
  clientOrgId: string;
  title: string;
  goal: string;
  deliverables: string[];
  constraints: string[];
  successCriteria: string[];
  startsAt: string;
  endsAt: string;
  connectionMode: 'isolated';
  access: {
    projectIds: string[];
    boardIds: string[];
    capabilities: MarketplaceEngagementCapability[];
  };
  grossCents: 0;
}

export interface MarketplaceExpertResponse {
  founderPlaceholder: boolean;
  note: string;
  payment: MarketplacePaymentState;
  expert: MarketplaceExpert;
  viewer: MarketplaceViewer;
  sellerGeo: MarketplaceSellerGeo | null;
  packs: MarketplacePack[];
  reviews: MarketplaceReview[];
}

/**
 * WHERE A SELLER'S MONEY WOULD GO — ONE type for one table, filled by two routes.
 *
 * `GET /marketplace/seller` has carried this since W6 and says `connected` (a boolean
 * about whether a processor reference exists). M2's `GET /marketplace/seller/connect/
 * status` says `processorAccountRef` (the reference itself) because the onboarding
 * screen has to show which account it is resuming.
 *
 * BOTH FIELDS LIVE ON ONE INTERFACE rather than on two similarly-named ones. A second
 * type for one row is the drift this file's header is about: the day somebody adds a
 * field, they add it to one of the two and the screens disagree about the same seller.
 * So the newer fields are OPTIONAL and each route fills what it knows.
 *
 * `requirements` is the PROCESSOR's own shape, which is why it is a union: the older
 * route hands over the raw jsonb column, the newer one hands over the adapter's list of
 * outstanding requirement names. Neither is interpreted here — a requirement is a string
 * the processor chose and the screen shows it.
 */
export interface MarketplacePayoutAccount {
  id: string;
  country: string;
  processor: string;
  /** Older route. True when a processor reference exists. */
  connected?: boolean;
  /** M2 route. The reference itself, or null before onboarding has produced one. */
  processorAccountRef?: string | null;
  status: string;
  /** FROZEN by the database when the row was written (0223). Never recomputed. */
  geoVerdict: string | null;
  payoutsEnabled: boolean;
  requirements: Record<string, unknown> | string[];
  updatedAt?: string | null;
}

export interface MarketplaceSellerResponse {
  founderPlaceholder: boolean;
  note: string;
  payment: MarketplacePaymentState;
  accountId?: string;
  profile: MarketplaceExpert | null;
  /** 'no_owned_account' and 'no_profile' are different facts. Never merge them. */
  reason: string | null;
  sellerGeo: MarketplaceSellerGeo | null;
  payoutAccount: MarketplacePayoutAccount | null;
  packs: MarketplacePack[];
}

export interface MarketplaceOrder {
  id: string;
  status: string;
  currency: string;
  gross_cents: number;
  commission_bps_applied: number;
  commission_cents: number;
  seller_net_cents: number;
  policy_fingerprint: string;
  refund_window_days_applied: number;
  payout_hold_days_applied: number;
  seller_geo_verdict_applied?: string | null;
  paid_at: string | null;
  pack_title?: string;
  pack_id?: string;
  /** False when the pack was removed with its publisher's account. The
   *  order itself is retained on purpose — it is the money record. */
  pack_available?: boolean;
  created_at?: string;
  // ── the detail route's extra columns (GET /orders/:orderId) ───────────────
  // Optional because the LIST route does not carry them, and a required field the list
  // cannot fill would force the list to invent one.
  tax_cents?: number;
  policy_frozen_at?: string | null;
  seller_country_applied?: string | null;
  /** The last day a refund can be asked for, derived by the database from the frozen
   *  window. Never recomputed in the browser. */
  refund_deadline?: string | null;
  /** When the hold until acceptance ends. Derived, and never the other word. */
  transfer_eligible_at?: string | null;
  buyer_account_id?: string | null;
  seller_account_id?: string | null;
  price_type?: string;
}

export interface MarketplaceOrderCreated {
  order: MarketplaceOrder;
  reused: boolean;
  payment: MarketplacePaymentState;
}

export interface MarketplaceSellerProfileInput {
  handle: string;
  displayName: string;
  headline?: string;
  bioMd?: string;
  skillTags?: string[];
  languages?: string[];
  country?: string;
  timezone?: string;
  rateHintCents?: number | null;
  rateHintUnit?: string | null;
}

/**
 * "This deployment has the marketplace switched off" vs "something broke".
 *
 * The gateway answers 404 `feature_disabled` when the requested runtime stage
 * is dark. That is configuration, not a fault, and screens say so — a dark
 * capability that renders as a crash teaches the wrong thing.
 */
export function isMarketplaceDisabled(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404 && error.code === 'feature_disabled';
}

export const getMarketplaceCapabilities = (): Promise<MarketplaceCapabilitiesResponse> =>
  apiJson<MarketplaceCapabilitiesResponse>('/marketplace/capabilities');

export const getMarketplacePolicy = (params: {
  scope: 'store' | 'talent' | 'both';
  grossCents?: number;
  country?: string;
}): Promise<MarketplacePolicyResponse> => {
  const q = new URLSearchParams();
  q.set('scope', params.scope);
  if (params.grossCents !== undefined) q.set('grossCents', String(params.grossCents));
  if (params.country) q.set('country', params.country);
  return apiJson<MarketplacePolicyResponse>(`/marketplace/policy?${q.toString()}`);
};

export const listMarketplaceExperts = (params?: {
  q?: string;
}): Promise<MarketplaceExpertsResponse> => {
  const q = new URLSearchParams();
  if (params?.q) q.set('q', params.q);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiJson<MarketplaceExpertsResponse>(`/marketplace/experts${suffix}`);
};

export const listMarketplacePacks = (params?: {
  q?: string;
  category?: string;
}): Promise<MarketplacePacksResponse> => {
  const s = new URLSearchParams();
  if (params?.q) s.set('q', params.q);
  if (params?.category) s.set('category', params.category);
  const suffix = s.toString() ? `?${s.toString()}` : '';
  return apiJson<MarketplacePacksResponse>(`/marketplace/packs${suffix}`);
};

export const getMarketplacePack = (packId: string): Promise<MarketplacePackResponse> =>
  apiJson<MarketplacePackResponse>(`/marketplace/packs/${packId}`);

export const getMarketplaceExpert = (handle: string): Promise<MarketplaceExpertResponse> =>
  apiJson<MarketplaceExpertResponse>(`/marketplace/experts/${handle}`);

export const getMarketplaceSeller = (): Promise<MarketplaceSellerResponse> =>
  apiJson<MarketplaceSellerResponse>('/marketplace/seller');

export const saveMarketplaceSeller = (
  input: MarketplaceSellerProfileInput,
): Promise<{ profile: MarketplaceExpert; sellerGeo: MarketplaceSellerGeo | null }> =>
  apiJson('/marketplace/seller', { method: 'PUT', body: JSON.stringify(input) });

export const publishMarketplaceSeller = (
  publish: boolean,
): Promise<{ profile: MarketplaceExpert }> =>
  apiJson('/marketplace/seller/publish', { method: 'POST', body: JSON.stringify({ publish }) });

export const createMarketplaceOrder = (packId: string): Promise<MarketplaceOrderCreated> =>
  apiJson('/marketplace/orders', { method: 'POST', body: JSON.stringify({ packId }) });

export const listMarketplaceOrders = (
  side: 'buying' | 'selling' = 'buying',
): Promise<{ side: string; orders: MarketplaceOrder[]; payment: MarketplacePaymentState }> =>
  apiJson(`/marketplace/orders?side=${side}`);

// ── the authoring side ────────────────────────────────────────────────────
// Nothing below computes a price, a commission or a rating. A pack's split is
// quoted by the gateway from the same SQL function the order freeze uses, and a
// rating comes back from the rollup trigger. The screens format and display.

export interface MarketplacePackDraftInput {
  slug: string;
  title: string;
  summary: string;
  descriptionMd?: string;
  category?: string;
  roleDirection?: string;
  tags?: string[];
}

export const getMarketplaceSellerPack = (packId: string): Promise<MarketplaceSellerPackResponse> =>
  apiJson<MarketplaceSellerPackResponse>(`/marketplace/seller/packs/${packId}`);

export const createMarketplacePack = (
  input: MarketplacePackDraftInput,
): Promise<{ pack: MarketplacePack }> =>
  apiJson('/marketplace/seller/packs', { method: 'POST', body: JSON.stringify(input) });

export const updateMarketplacePack = (
  packId: string,
  input: Omit<MarketplacePackDraftInput, 'slug'>,
): Promise<{ pack: MarketplacePack }> =>
  apiJson(`/marketplace/seller/packs/${packId}`, { method: 'PUT', body: JSON.stringify(input) });

export const addMarketplacePackVersion = (
  packId: string,
  input: { version: string; changelogMd?: string },
): Promise<{ version: MarketplacePackVersion }> =>
  apiJson(`/marketplace/seller/packs/${packId}/versions`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

/**
 * The skills THIS Workspace has, which are the only ones a pack may carry.
 *
 * `orgId` is the active Workspace, passed explicitly rather than inferred: the
 * gateway puts it through org-auth.ts and answers 404 for a Workspace the
 * caller is not in, so a stale value in this browser cannot reach another
 * tenant's catalogue.
 */
export const listMarketplaceWorkspaceSkills = (
  orgId: string,
  q?: string,
): Promise<{ orgId: string; skills: MarketplaceWorkspaceSkill[]; maxItems: number }> =>
  apiJson(
    `/marketplace/seller/workspace-skills?orgId=${encodeURIComponent(orgId)}` +
      (q ? `&q=${encodeURIComponent(q)}` : ''),
  );

/**
 * Assemble a version out of Workspace skills, in this order.
 *
 * Whole set, not a patch — order is part of the fingerprint. The response
 * carries the fingerprint the database produced for the write; nothing in this
 * browser computes one.
 */
export const setMarketplacePackContents = (
  packId: string,
  versionId: string,
  input: { orgId: string; skillIds: string[] },
): Promise<MarketplacePackContents> =>
  apiJson(`/marketplace/seller/packs/${packId}/versions/${versionId}/contents`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });

export const getMarketplacePackContents = (
  packId: string,
  versionId: string,
): Promise<MarketplacePackContents> =>
  apiJson(`/marketplace/seller/packs/${packId}/versions/${versionId}/contents`);

export const setMarketplacePackPrice = (
  packId: string,
  input: { priceType: 'free' | 'one_time'; amountCents: number },
): Promise<{
  price: MarketplacePrice;
  priceVerdict: string;
  quote: MarketplaceQuote | null;
  payment: MarketplacePaymentState;
}> =>
  apiJson(`/marketplace/seller/packs/${packId}/prices`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

/**
 * The Store mirror of `billing.demoPrice` (billing.ts) — founder-only,
 * mock-provider-only, sets `chaos.skill_pack_prices.mock_only` on one price
 * row so a mock Store checkout can be rehearsed at all (I18). See
 * `POST /api/marketplace/prices/demo-price` in marketplace-routes.ts.
 */
export const setMarketplacePackPriceDemo = (
  priceId: string,
  mockOnly: boolean,
): Promise<{ priceId: string; packId: string; mockOnly: boolean }> =>
  apiJson('/marketplace/prices/demo-price', {
    method: 'POST',
    body: JSON.stringify({ priceId, mockOnly }),
  });

export const publishMarketplacePack = (
  packId: string,
  publish: boolean,
): Promise<{ pack: MarketplacePack }> =>
  apiJson(`/marketplace/seller/packs/${packId}/publish`, {
    method: 'POST',
    body: JSON.stringify({ publish }),
  });

// ── hiring ────────────────────────────────────────────────────────────────

export const createMarketplaceEngagement = (
  input: MarketplaceDarkEngagementProposal,
): Promise<{
  engagement: MarketplaceEngagement;
  reused: boolean;
  payment: MarketplacePaymentState;
}> => apiJson('/marketplace/engagements', { method: 'POST', body: JSON.stringify(input) });

export const listMarketplaceEngagements = (
  side: 'client' | 'expert' = 'client',
): Promise<{
  side: string;
  engagements: MarketplaceEngagement[];
  payment: MarketplacePaymentState;
  reason?: string;
}> => apiJson(`/marketplace/engagements?side=${side}`);

export const moveMarketplaceEngagement = (
  engagementId: string,
  status: string,
): Promise<{ engagement: MarketplaceEngagement; side: string }> =>
  apiJson(`/marketplace/engagements/${engagementId}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });

// ── reviews ───────────────────────────────────────────────────────────────

/**
 * The response carries the ROLLUP the database computed, not an average this
 * browser worked out from the reviews it happens to be holding. Two averages
 * would disagree the first time a moderator hid one.
 */
export const submitMarketplaceReview = (
  input:
    | { subjectKind: 'pack'; packId: string; rating: number; bodyMd?: string }
    | { subjectKind: 'expert'; expertHandle: string; rating: number; bodyMd?: string },
): Promise<{ review: MarketplaceReview; rating: number | null; ratingCount: number }> =>
  apiJson('/marketplace/reviews', { method: 'POST', body: JSON.stringify(input) });

// ── reports ───────────────────────────────────────────────────────────────
//
// Anybody signed in may report a published pack, a review, or an expert
// profile — POST /api/marketplace/reports. It is idempotent per (reporter,
// subject, reason): reporting the same thing twice answers `opened: false`
// rather than opening a second case, and the screen has to say so instead of
// thanking the person twice.

export interface MarketplaceReportInput {
  subjectKind: 'pack' | 'review' | 'expert_profile';
  subjectId: string;
  reason: string;
}

export const submitMarketplaceReport = (
  input: MarketplaceReportInput,
): Promise<{ caseId: string; opened: boolean }> =>
  apiJson('/marketplace/reports', { method: 'POST', body: JSON.stringify(input) });

// ── moderation ────────────────────────────────────────────────────────────
//
// GET .../moderation/queue, GET .../moderation/cases/:id and POST
// .../cases/:id/decide are all `requireSystemFounder` on the gateway
// (marketplace-routes.ts #33-35) — read from `store.moderator_is_founder`,
// not hard-coded, so the day the founder appoints somebody else there is one
// place that changes. The client-side fence the two moderation screens use is
// `useIsSystemFounder()` from `lib/active-org`, the SAME signal the founder-
// only global-catalog CRUD already gates on.

export type ModerationSubjectKind = 'pack' | 'pack_version' | 'review' | 'expert_profile';
export type ModerationDecision = 'approve' | 'dismiss' | 'hide' | 'takedown' | 'suspend';
export type ModerationCaseStateFilter = 'open' | 'decided' | 'withdrawn' | 'all';

/** `caseToApi()` in marketplace-moderation.ts. `decidedBy` never crosses the wire. */
export interface ModerationCase {
  id: string;
  caseKey: string;
  subjectKind: string;
  subjectId: string;
  packId: string | null;
  versionId: string | null;
  subjectLabel: string;
  openedByKind: string;
  openedReason: string;
  detail: Record<string, unknown>;
  slaHours: number;
  openedAt: string;
  /** Frozen at open by the database — never recomputed here. */
  slaDueAt: string;
  /** Computed by the DATABASE against now(). Never derived on the client. */
  overdue: boolean;
  state: string;
  decision: string | null;
  decidedAt: string | null;
}

export interface ModerationQueueResponse {
  cases: ModerationCase[];
  moderatorIsFounder: boolean;
  note: string;
}

/** One row of `chaos.store_moderation_decisions` — an append-only ledger. */
export interface ModerationDecisionRecord {
  id: string;
  decision: string;
  notes: string;
  decided_at: string;
  from_state: string;
  to_state: string;
}

/** One row of `chaos.skill_pack_conduct_events` for the case's pack, if any. */
export interface ModerationConductEvent {
  id: string;
  event_key: string;
  kind: string;
  severity: string;
  summary: string;
  detail: Record<string, unknown>;
  org_id: string | null;
  account_id: string | null;
  moderation_case_id: string | null;
  buyer_cleanliness_debited: boolean;
  created_at: string;
  pack_slug_snapshot: string | null;
  version_semver_snapshot: string | null;
}

/**
 * One row of `chaos.store_moderation_overlays` — the ENFORCEMENT record, which
 * is append-only in the database: a block is lifted, never deleted. `appliedBy`
 * and `liftedBy` never cross the wire, same rule as `decidedBy`.
 */
export interface ModerationOverlay {
  id: string;
  packId: string;
  versionId: string | null;
  severity: 'listing_only' | 'update_block' | 'runtime_block' | 'legal_remove' | string;
  reason: string;
  caseId: string | null;
  appliedAt: string;
  liftedAt: string | null;
  liftReason: string;
  active: boolean;
  packSlug: string;
  versionSemver: string;
}

export interface ModerationCaseResponse {
  case: ModerationCase;
  decisions: ModerationDecisionRecord[];
  conduct: ModerationConductEvent[];
  /** Every overlay on this case's PACK, active first. `[]` when unreadable. */
  overlays: ModerationOverlay[];
  /**
   * false = the overlay relation is not reachable on this gateway (0348 not
   * applied, or the stage is dark). An empty list and an unavailable relation
   * are different sentences and the screen must not merge them.
   */
  overlaysReadable: boolean;
}

export interface ModerationOverlayLiftResponse {
  overlay: ModerationOverlay;
  effect: 'block_lifted';
  note: string;
}

export interface ModerationDecideResponse {
  case: ModerationCase;
  effect: 'none' | 'pack_withdrawn' | 'review_hidden' | 'expert_suspended';
}

export const listModerationQueue = (params?: {
  state?: ModerationCaseStateFilter;
  limit?: number;
}): Promise<ModerationQueueResponse> => {
  const q = new URLSearchParams();
  if (params?.state) q.set('state', params.state);
  if (params?.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiJson<ModerationQueueResponse>(`/marketplace/moderation/queue${suffix}`);
};

export const getModerationCase = (caseId: string): Promise<ModerationCaseResponse> =>
  apiJson<ModerationCaseResponse>(`/marketplace/moderation/cases/${caseId}`);

export const decideModerationCase = (
  caseId: string,
  input: { decision: ModerationDecision; notes?: string },
): Promise<ModerationDecideResponse> =>
  apiJson(`/marketplace/moderation/cases/${caseId}/decide`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

/**
 * Lift a block. The reason is REQUIRED — `store_moderation_overlays_lift_shape_check`
 * refuses a lift without one, and the gateway answers 422 `lift_reason_required`
 * rather than letting that reach anybody as a 500. The screen therefore asks for
 * it before it sends.
 */
export const liftModerationOverlay = (
  overlayId: string,
  input: { reason: string },
): Promise<ModerationOverlayLiftResponse> =>
  apiJson(`/marketplace/moderation/overlays/${overlayId}/lift`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

// ── admin governance (founder-only) ──────────────────────────────────────
//
// GET /api/admin/marketplace-policy and GET /api/admin/marketplace-seller-
// countries are read-only, `requireFounder`-gated views over
// chaos.marketplace_policy and chaos.marketplace_seller_countries — the two
// tables migration 0189 seeded with real triggers and real RAISEs behind them
// (docs/platform/POLICY_VALUE_READERS.tsv) and that had no admin-facing
// reader before this change. Read-only on purpose, same rule
// platform-policy-routes.ts states for member-write-policy and
// founder-placeholders: changing one of these values is a deliberate UPDATE
// against the database, not a button in this screen.

export interface AdminMarketplacePolicyRow {
  scope: 'store' | 'talent' | 'both';
  key: string;
  valueKind: string;
  valueNum: string | null;
  valueBool: boolean | null;
  valueText: string | null;
  valueList: string[] | null;
  origin: string;
  founderPlaceholder: boolean;
  decisionSlot: string | null;
  canonRef: string | null;
  canonQuote: string | null;
  rationale: string | null;
  notes: string | null;
}

export interface AdminMarketplacePolicyResponse {
  rows: AdminMarketplacePolicyRow[];
  count: number;
  placeholderCount: number;
}

export const getAdminMarketplacePolicy = (): Promise<AdminMarketplacePolicyResponse> =>
  apiJson<AdminMarketplacePolicyResponse>('/admin/marketplace-policy');

export interface AdminSellerCountryRow {
  countryCode: string;
  region: string;
  paidSalesAllowed: boolean;
  freePublishingAllowed: boolean;
  payoutProvider: string | null;
  stripeConnectVerified: boolean;
  origin: string;
  founderPlaceholder: boolean;
  canonRef: string | null;
  canonQuote: string | null;
  reason: string | null;
  updatedAt: string;
}

export interface AdminSellerCountriesResponse {
  countries: AdminSellerCountryRow[];
  count: number;
  placeholderCount: number;
}

export const getAdminMarketplaceSellerCountries = (): Promise<AdminSellerCountriesResponse> =>
  apiJson<AdminSellerCountriesResponse>('/admin/marketplace-seller-countries');

// ── pack guard policy (founder-only) ─────────────────────────────────────
//
// GET/PUT /api/marketplace/moderation/packs/:packId/guard-policy — the PACK
// layer of the guard stack (defaults ← workspace ← pack, migration 0272
// W6-19). The gateway route existed with a working GET and a working,
// server-validated PUT; nothing in this app ever called either one.

/** One row of `chaos.guard_policy_defaults` — the catalog a pack override may
 * draw a key from, and the type `guard_policy_overrides_validate()` checks a
 * write against. */
export interface GuardPolicyDefaultRow {
  key: string;
  value: unknown;
  valueType: 'boolean' | 'integer' | 'text' | 'string_array' | 'json_array';
  origin: string;
  canonRef: string | null;
  placeholder: boolean;
  note: string;
}

export interface PackGuardPolicyResponse {
  packId: string;
  overrides: Record<string, unknown>;
  reason: string;
  updatedAt: string | null;
  /** Only present when the GET carried a valid `orgId` query param. */
  resolvedForOrg: Record<string, unknown> | null;
  seeded: GuardPolicyDefaultRow[];
}

export const getPackGuardPolicy = (
  packId: string,
  orgId?: string,
): Promise<PackGuardPolicyResponse> =>
  apiJson<PackGuardPolicyResponse>(
    `/marketplace/moderation/packs/${packId}/guard-policy${orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''}`,
  );

export type GuardPolicyRefusalCode = 'unknown_key' | 'type_mismatch';

export const putPackGuardPolicy = (
  packId: string,
  input: { overrides: Record<string, unknown>; reason: string },
): Promise<{ packId: string; overrides: Record<string, unknown>; reason: string }> =>
  apiJson(`/marketplace/moderation/packs/${packId}/guard-policy`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });

// ══════════════════════════════════════════════════════════════════════════
// M2 — THE MONEY CALLS
// ══════════════════════════════════════════════════════════════════════════
// STILL NO ARITHMETIC. Every figure below was computed by a SQL function and frozen on
// a row; the screens format cents into a string and do nothing else to them. A
// percentage computed in the browser would be a second implementation of the corridor,
// and exactly one of the two gets edited the day the founder moves it.
//
// AND NO MONEY RULE IS DECIDED HERE. Which lane a refund is in, whether a payout may be
// opened, whether live money is armed — all of them come back as a value from the
// server. The bundle carries no ceiling, no window and no rate.

/** What `POST /orders/:id/pay` answers. It creates a checkout; it does not pay. */
export interface MarketplaceCheckoutSession {
  checkoutRef: string;
  /** In-app for the mock (`/checkout/mock/<ref>`), hosted for a real processor. */
  redirectUrl: string;
  mode: 'test' | 'live';
  provider: 'mock' | 'stripe';
  /** I18. A demo checkout exists only for a price marked mock_only. */
  mockOnly: boolean;
  amountCents: number;
  currency: string;
  /** True when the reference was already on the order and this call re-used it. */
  reused: boolean;
}

export interface MarketplaceCheckoutResponse {
  checkout: MarketplaceCheckoutSession;
  payment: MarketplacePaymentState;
  note: string;
}

/** What the rate limiter answered, so a refusal can be explained rather than shrugged. */
export interface MarketplaceCheckoutRate {
  allowed: boolean;
  reason: string | null;
  accountAttempts: number;
  ipAttempts: number;
  ceiling: number;
  windowHours: number;
}

/**
 * One order, as the buyer or the seller sees it.
 *
 * The route has existed since W6 and had no client: the checkout screen listed orders and
 * never opened one. M2 needs it, because the refund affordance and the honest return page
 * both report on a SINGLE order's frozen figures.
 */
export const getMarketplaceOrder = (
  orderId: string,
): Promise<{ order: MarketplaceOrder; payment: MarketplacePaymentState; note: string }> =>
  apiJson(`/marketplace/orders/${orderId}`);

export const payMarketplaceOrder = (orderId: string): Promise<MarketplaceCheckoutResponse> =>
  apiJson(`/marketplace/orders/${orderId}/pay`, { method: 'POST' });

/**
 * Complete a MOCK checkout.
 *
 * This is the mock provider's callback, asked for by the browser because the mock has no
 * server of its own. It marks nothing: the gateway signs an event with the vault secret
 * and drives it through the real webhook handler, so the dedup row, the signature check
 * and the one applier all still stand. On `BILLING_PROVIDER=stripe` it is refused.
 */
export const settleMockMarketplaceOrder = (
  orderId: string,
): Promise<{ delivered: boolean; eventId: string }> =>
  apiJson(`/marketplace/orders/${orderId}/mock-settle`, { method: 'POST' });

/** 0189's three lanes. `window_closed` never becomes a refund. */
export type MarketplaceRefundLane = 'self_service' | 'moderator_review' | 'window_closed';

export interface MarketplaceRefundRequested {
  lane: MarketplaceRefundLane;
  amountCents: number;
  productionAttached: boolean;
  refundDeadline: string | null;
  /**
   * G9. `enforced` is always false and the screen must not present it as a decision the
   * system has taken — whether a refund is allowed is a moderator's call.
   */
  abuse: {
    verdict: 'ordinary' | 'serial_refunder';
    refunds90d: number;
    threshold: number;
    enforced: false;
  } | null;
  note: string;
}

export const requestMarketplaceRefund = (
  orderId: string,
  body: { amountCents?: number; reason?: string } = {},
): Promise<MarketplaceRefundRequested> =>
  apiJson(`/marketplace/orders/${orderId}/refund`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export interface MarketplacePurchase {
  orderId: string;
  status: string;
  packId: string | null;
  packTitle: string | null;
  packAvailable: boolean;
  currency: string;
  grossCents: number;
  commissionCents: number;
  paidAt: string | null;
  refundDeadline: string | null;
  refundedAt: string | null;
  /** `succeeded` reversals only — a pending reservation has returned nobody anything. */
  refundedCents: number;
  installed: boolean;
}

export const listMarketplacePurchases = (): Promise<{
  purchases: MarketplacePurchase[];
  payment?: MarketplacePaymentState;
  reason?: string;
  note?: string;
}> => apiJson('/marketplace/me/purchases');

export interface MarketplaceEarnings {
  refs: number;
  legs: number;
  currency: string | null;
  currencies: number;
  holdCents: number;
  payableCents: number;
  grossCents: number;
  commissionCents: number;
  reversedCents: number;
  refundedToBuyerCents: number;
  /**
   * Leg counts per source journal, carried to the screen ON PURPOSE: a sum over one
   * journal is indistinguishable from a correct zero when the other is empty, and the
   * talent side has no writer until M3d.
   */
  bySource: Record<string, number>;
}

export interface MarketplacePayoutLine {
  id: string;
  orderId: string;
  status: string;
  amountCents: number;
  currency: string;
  failureCode: string | null;
  createdAt: string | null;
  settledAt: string | null;
}


export interface MarketplaceEarningsResponse {
  earnings: MarketplaceEarnings | null;
  payouts?: MarketplacePayoutLine[];
  /** The next hold to end, as a DATE the database derived from the frozen day count. */
  nextHold?: { at: string | null; orders: number; cents: number };
  payoutAccount?: MarketplacePayoutAccount | null;
  payment?: MarketplacePaymentState;
  reason?: string;
  note?: string;
}

export const getMarketplaceEarnings = (): Promise<MarketplaceEarningsResponse> =>
  apiJson('/marketplace/me/earnings');

export interface MarketplacePackPrice {
  id: string;
  price_type: string;
  amount_cents: number;
  currency: string;
  active: boolean;
  created_at?: string;
}

export const listMarketplacePackPrices = (
  packId: string,
): Promise<{ packId: string; currentPriceId: string | null; prices: MarketplacePackPrice[]; note: string }> =>
  apiJson(`/marketplace/packs/${packId}/prices`);

/** Deactivation only. Re-activating an old price is repricing with extra steps. */
export const deactivateMarketplacePackPrice = (
  packId: string,
  priceId: string,
): Promise<{ price: MarketplacePackPrice }> =>
  apiJson(`/marketplace/packs/${packId}/prices/${priceId}`, {
    method: 'PATCH',
    body: JSON.stringify({ active: false }),
  });

export interface MarketplaceSellerOnboardingChecklist {
  has_profile: boolean;
  profile_published: boolean;
  agreement_accepted: boolean;
  has_pack: boolean;
  pack_published: boolean;
  has_active_price: boolean;
  payout_destination: boolean;
  payouts_enabled: boolean;
  payout_geo_verdict: string | null;
}

export const getMarketplaceSellerOnboarding = (): Promise<{
  checklist: MarketplaceSellerOnboardingChecklist | null;
  payoutAccount?: MarketplacePayoutAccount | null;
  payment?: MarketplacePaymentState;
  reason?: string;
  note?: string;
}> => apiJson('/marketplace/seller/onboarding');

export const startMarketplaceSellerConnect = (): Promise<{
  payoutAccount: MarketplacePayoutAccount;
  onboardingUrl: string;
  expiresAt: string;
  mode: 'test' | 'live';
  payment: MarketplacePaymentState;
}> => apiJson('/marketplace/seller/connect/onboard', { method: 'POST' });

export const getMarketplaceSellerConnectStatus = (): Promise<{
  payoutAccount: MarketplacePayoutAccount | null;
  payment?: MarketplacePaymentState;
  reason?: string;
}> => apiJson('/marketplace/seller/connect/status');

/**
 * G11. Records WHICH terms were accepted, as a sha256 of the document.
 *
 * The digest is computed by the caller over the exact text that was SHOWN, which is the
 * whole point: a hash of something else answers "which terms" with the wrong document.
 */
export const acceptMarketplaceSellerAgreement = (input: {
  agreementKey: string;
  version: string;
  documentSha256: string;
}): Promise<{ agreement: Record<string, unknown> | null; created: boolean }> =>
  apiJson('/marketplace/seller/agreement', { method: 'POST', body: JSON.stringify(input) });

export interface MarketplacePaymentsAdmin {
  authority: { ready: boolean; blockedBy: string | null; reason: string };
  provider: 'mock' | 'stripe';
  liveMoneyArmed: boolean;
  events: Array<{ status: string; n: number }>;
  deferred: Array<Record<string, unknown>>;
  discrepancies: Array<Record<string, unknown>>;
  reconciliation: {
    runs: Array<Record<string, unknown>>;
    openDiscrepancies: number;
    liveMoneyArmed: boolean;
  };
  payment: MarketplacePaymentState;
}

export const getMarketplacePaymentsAdmin = (): Promise<MarketplacePaymentsAdmin> =>
  apiJson('/marketplace/admin/payments/reconciliation');

// ── M3d: the talent side's money ────────────────────────────────────────────
//
// NOTHING HERE IS COMPUTED. `payableCents` is `chaos.engagement_payable_net()`,
// `transferEligibleAt` is the date the database derived from the hold length FROZEN at
// creation, and the payment rows are the journal's own sources. A "days remaining"
// number in the browser would be a second implementation of the hold, and the founder
// moves that with one UPDATE (I3).
export interface EngagementMoneyPayment {
  kind: string;
  status: string;
  amountCents: number;
  currency: string;
  createdAt: string;
}

export interface EngagementMoney {
  engagementId: string;
  status: string;
  grossCents: number;
  currency: string;
  /** Null until a signed processor event says the client's card settled. */
  paidAt: string | null;
  processorMode: string | null;
  /** True once Pay has opened a processor session. The reference itself stays server-side. */
  checkoutStarted: boolean;
  holdDays: number;
  /** When the expert becomes payable. Null until somebody accepts the delivery. */
  transferEligibleAt: string | null;
  /** What is still theirs after every settled reversal. From SQL, never subtracted here. */
  payableCents: number | null;
  payments: EngagementMoneyPayment[];
  payout: { status: string; amountCents: number; settledAt: string | null } | null;
}

export interface EngagementMoneyResponse {
  money: EngagementMoney;
  payment?: MarketplacePaymentState;
  note?: string;
}

export async function getEngagementMoney(id: string): Promise<EngagementMoneyResponse> {
  return apiJson<EngagementMoneyResponse>(
    `/marketplace/engagements/${encodeURIComponent(id)}/money`,
  );
}

export interface EngagementCheckoutResponse {
  checkout: {
    checkoutRef: string;
    redirectUrl: string;
    mode: string;
    amountCents: number;
    currency: string;
  };
  /** ALWAYS false on this response. The money moves when a signed event arrives. */
  paid: boolean;
  payment?: MarketplacePaymentState;
}

export async function startEngagementCheckout(id: string): Promise<EngagementCheckoutResponse> {
  return apiJson<EngagementCheckoutResponse>(
    `/marketplace/engagements/${encodeURIComponent(id)}/checkout`,
    { method: 'POST' },
  );
}

export interface EngagementMoveWithMoney {
  engagement: MarketplaceEngagement;
  side: 'client' | 'expert';
  money: EngagementMoney | null;
  effect?: string;
}

/** The client accepts the delivery. This is what starts the hold clock. */
export async function acceptEngagementDelivery(id: string): Promise<EngagementMoveWithMoney> {
  return apiJson<EngagementMoveWithMoney>(
    `/marketplace/engagements/${encodeURIComponent(id)}/accept`,
    { method: 'POST' },
  );
}

export interface EngagementMockSettleResponse {
  delivered: boolean;
  eventId?: string;
  money?: EngagementMoney | null;
  error?: string;
}

/**
 * THE DEMO'S CALLBACK, and it is not a second way to pay.
 *
 * On `BILLING_PROVIDER=mock` the processor has no server, so nothing ever arrives at
 * the webhook and an engagement could never become paid — which made the Charged date,
 * the payment rows and the Dispute button (gated on `paidAt`) unreachable on the only
 * box that exists. This asks the gateway to SIGN the event it would have received and
 * drive it through the real webhook handler, so the signature check, the dedup row and
 * the one applier all still stand. On any other provider it answers 409.
 */
export async function mockSettleEngagement(id: string): Promise<EngagementMockSettleResponse> {
  return apiJson<EngagementMockSettleResponse>(
    `/marketplace/engagements/${encodeURIComponent(id)}/mock-settle`,
    { method: 'POST' },
  );
}

/** Raise a dispute. It holds the payout and refunds nothing — the response says so. */
export async function disputeEngagement(id: string): Promise<EngagementMoveWithMoney> {
  return apiJson<EngagementMoveWithMoney>(
    `/marketplace/engagements/${encodeURIComponent(id)}/dispute`,
    { method: 'POST' },
  );
}

// ══════════════════════════════════════════════════════════════════════════
// M4 — THE BUYER'S HALF, THE TRIALS, THE PUBLISHER SHELF AND THE SCREENING VIEW
// ══════════════════════════════════════════════════════════════════════════
// Every figure below arrives from the server. Nothing on these screens computes a
// price, a rate or a date: the numbers are founder placeholders and he moves them
// with one UPDATE (I3).

export interface MarketplaceLibraryEntry {
  id: string;
  orderId: string | null;
  /** null is NOT missing: it means the pack it names is gone and the snapshots remain. */
  packId: string | null;
  versionId: string | null;
  packSlug: string;
  packTitle: string;
  version: string;
  fingerprint: string | null;
  entitlement: string;
  state: string;
  /**
   * WHY THE LICENCE ENDED, when it was not the buyer who ended it.
   *
   * Non-null exactly when `state === 'revoked'` (0387), which is the money path's
   * state: the order behind this entry was refunded or charged back. Carried so the
   * screen can say "your money came back and so did the licence" instead of leaving a
   * pack to look as though it had vanished.
   */
  revokedReason: string | null;
  acquiredAt: string;
  activatedAt: string | null;
  uninstalledAt: string | null;
  /** Where it is AVAILABLE. Availability is not activation (SPEC-16 §3.7.3). */
  workspaces: Array<{
    orgId: string;
    orgName: string | null;
    state: string;
    boundAt: string;
  }>;
}

export const listMarketplaceLibrary = (): Promise<{
  installs: MarketplaceLibraryEntry[];
  note?: string;
}> => apiJson('/marketplace/library');

/**
 * A STATE, NOT A DELETION (SPEC-16 §8). The response carries the gateway's own
 * sentence about what is and is not reached by it, and the screen prints that rather
 * than a paraphrase.
 */
export const uninstallMarketplacePack = (
  installId: string,
): Promise<{
  install: MarketplaceLibraryEntry;
  runtime?: { closed: number; sessionsMarked: number };
  alreadyUninstalled?: boolean;
  note?: string;
}> =>
  apiJson(`/marketplace/library/${installId}/uninstall`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export interface MarketplaceTrial {
  id: string;
  packId: string;
  versionId: string;
  packSlug: string;
  versionSemver: string;
  views: number;
  startedAt: string;
  expiresAt: string;
  expiredAt: string | null;
  /** The SERVER's answer, never a date comparison in the browser. */
  live: boolean;
  sandboxState: string | null;
  excerptChars: number;
  windowHours: number;
}

export const listMarketplaceTrials = (): Promise<{
  trials: MarketplaceTrial[];
  note?: string;
}> => apiJson('/marketplace/me/trials');

export const listPublisherPacks = (
  handle: string,
): Promise<{ handle: string; packs: MarketplacePack[]; note?: string }> =>
  apiJson(`/marketplace/publishers/${encodeURIComponent(handle)}/packs`);

export interface ScreeningStageView {
  ordinal: number;
  code: string;
  title: string;
  blocking: boolean;
  status: string | null;
  findings: number;
  completed_at: string | null;
}

export interface ScreeningFindingView {
  id: string;
  stage: string;
  code: string;
  severity: string;
  disposition: string;
  /** The seller-visible sentence. The restricted detail never reaches this app. */
  message: string;
  created_at: string;
}

export interface ScreeningRunView {
  id: string;
  version_id: string;
  manifest_sha256: string;
  scanner_set_version: string;
  status: string;
  result: string | null;
  created_at: string;
  completed_at: string | null;
  /** G13 — the hours, the date, and whether WE are the ones being waited on. */
  sla: {
    hours: number;
    due_at: string;
    awaiting_us: boolean;
    breached: boolean;
    open_findings: number;
  };
  stages: ScreeningStageView[];
  findings: ScreeningFindingView[];
}

export const getPackScreening = (
  packId: string,
): Promise<{ packId: string; screening: ScreeningRunView[]; note?: string }> =>
  apiJson(`/marketplace/packs/${packId}/screening`);

export const submitVersionForScreening = (
  packId: string,
  versionId: string,
): Promise<{ runId: string; screening: ScreeningRunView[]; note?: string }> =>
  apiJson(`/marketplace/seller/packs/${packId}/versions/${versionId}/submit`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export interface ScreeningQueueRow {
  run_id: string;
  pack_id: string | null;
  pack_slug: string;
  version_semver: string;
  result: string | null;
  open_findings: number;
  due_at: string;
  breached: boolean;
}

export const listScreeningQueue = (): Promise<{
  queue: ScreeningQueueRow[];
  slaNote?: string;
}> => apiJson('/marketplace/moderation/screening');

export const adjudicateScreeningFinding = (
  findingId: string,
  disposition: 'accepted' | 'waived' | 'resolved',
): Promise<{ findingId: string; disposition: string }> =>
  apiJson(`/marketplace/moderation/screening/findings/${findingId}`, {
    method: 'POST',
    body: JSON.stringify({ disposition }),
  });

export interface ModeratorRefundRow {
  orderId: string;
  packSlug: string;
  /** The buyer's PSEUDONYM. A moderator deciding a refund never needs a name. */
  buyerSubjectRef: string | null;
  grossCents: number;
  refundAmountCents: number;
  currency: string;
  processorMode: string | null;
  lane: string | null;
  paidAt: string | null;
  refundDeadline: string | null;
}

export const listModerationRefunds = (): Promise<{
  /** Which world the gateway is in. The list is scoped to it; see `otherModeStanding`. */
  processorMode?: string;
  requests: ModeratorRefundRow[];
  /**
   * REQUESTS THIS BOX CANNOT ASK A PROCESSOR FOR, because they belong to the other
   * world. A count and not a list: nothing can be done about them from here, and a
   * list would invite somebody to try.
   */
  otherModeStanding?: number;
  note?: string;
}> => apiJson('/marketplace/moderation/refunds');

/**
 * AN OPEN DISPUTE, AS THE PERSON WHO HAS TO DECIDE IT SEES IT.
 *
 * `payoutsStopped` is the whole cost of leaving the row open, on the same line as the
 * decision: 0379 refuses a payout while a dispute is unclosed, and until 0388 nothing
 * in the platform could close one — so a chargeback stopped that order's payout for
 * ever. The frozen fee comes from the instant the dispute opened and is never
 * re-derived.
 */
export interface OpenDisputeRow {
  disputeId: string;
  subjectKind: string;
  subjectId: string;
  processor: string;
  processorDisputeRef: string | null;
  amountCents: number;
  currency: string;
  feeBearer: string;
  feeCents: number;
  status: string;
  openedAt: string;
  evidenceDueAt: string | null;
  evidenceItems: number;
  payoutsStopped: number;
}

export const listModerationDisputes = (): Promise<{
  processorMode?: string;
  disputes: OpenDisputeRow[];
  note?: string;
}> => apiJson('/marketplace/moderation/disputes');

/**
 * RECORD A VERDICT. It moves no money — the chargeback already did that when it
 * arrived — and it is FINAL: a second, different outcome is refused with 409 rather
 * than overwriting a record the database keeps append-only on purpose.
 */
export const closeModerationDispute = (
  disputeId: string,
  outcome: 'won' | 'lost' | 'withdrawn',
  note?: string,
): Promise<{ disputeId: string; outcome: string; closedAt: string; already: boolean }> =>
  apiJson(`/marketplace/moderation/disputes/${disputeId}/close`, {
    method: 'POST',
    body: JSON.stringify({ outcome, note: note ?? '' }),
  });

// ══════════════════════════════════════════════════════════════════════════
// M4c — THE RUNTIME AND THE CONSENT
// ══════════════════════════════════════════════════════════════════════════

export interface SessionPackView {
  activationId: string;
  installId: string;
  packId: string | null;
  versionId: string | null;
  packSlug: string;
  versionSemver: string;
  /** What the pack ASKED FOR, frozen at activation. Shown before the button. */
  declaredRequirements: string[];
  activatedAt: string;
}

export const listSessionPacks = (
  sessionId: string,
): Promise<{ sessionId: string; packs: SessionPackView[] }> =>
  apiJson(`/marketplace/sessions/${sessionId}/packs`);

/**
 * `acknowledge` must name EVERY requirement the version declares, or the database
 * refuses (R41). The manifest is shown BEFORE this call — that is what the
 * acknowledgement is for — and this client never invents the list: it passes back
 * exactly what the refusal or the pack page handed it.
 */
export const activateSessionPack = (
  sessionId: string,
  installId: string,
  acknowledge: string[] = [],
): Promise<{
  sessionId: string;
  activationId: string | null;
  activated: boolean;
  declared: string[];
  packs: SessionPackView[];
  note?: string;
}> =>
  apiJson(`/marketplace/sessions/${sessionId}/packs`, {
    method: 'POST',
    body: JSON.stringify({ installId, acknowledge }),
  });

/**
 * The detach. It ends on the boundary of the CURRENT turn — the same contract a
 * detached role has carried since 2026-07-26 — and `sessionsToRebuild` is how many
 * containers will be recreated on their next prompt because of it.
 */
export const deactivateSessionPack = (
  sessionId: string,
  installId: string,
): Promise<{
  sessionId: string;
  closed: boolean;
  sessionsToRebuild: number;
  packs: SessionPackView[];
}> =>
  apiJson(`/marketplace/sessions/${sessionId}/packs/${installId}`, { method: 'DELETE' });

/** What is AVAILABLE in one Workspace. Availability is not activation. */
export const listWorkspacePacks = (
  orgId: string,
): Promise<{
  orgId: string;
  packs: Array<{
    installId: string;
    packId: string | null;
    packSlug: string;
    packTitle: string;
    version: string;
    fingerprint: string | null;
    state: string;
    boundAt: string;
  }>;
}> => apiJson(`/marketplace/workspace-packs?orgId=${encodeURIComponent(orgId)}`);

/**
 * R48 — a PARTY grants a scope for a window. The operator's ability to look is
 * decided by `chaos.dispute_evidence_readable()`, which takes no operator identity
 * at all, so there is nothing here that could be a bypass.
 */
export type DisputeEvidenceScope =
  | 'order_metadata'
  | 'pack_manifest'
  | 'engagement_brief'
  | 'delivery_receipt'
  | 'transcript'
  | 'storage_object';

export const grantDisputeEvidenceConsent = (
  disputeId: string,
  scope: DisputeEvidenceScope[],
  hours?: number,
): Promise<{
  disputeId: string;
  consentId: string | null;
  scope: string[];
  note?: string;
}> =>
  apiJson(`/marketplace/disputes/${disputeId}/evidence-consent`, {
    method: 'POST',
    body: JSON.stringify({ scope, hours }),
  });

export interface MarketplaceDispute {
  id: string;
  subjectKind: string;
  subjectId: string;
  /** Which side of the argument this account is on. */
  side: string;
  status: string;
  amountCents: number;
  currency: string;
  /** FROZEN when the dispute opened. The screen prints it and derives nothing. */
  feeBearer: string;
  feeCents: number;
  openedAt: string;
  closedAt: string | null;
  evidenceItems: number;
  consentScope: string[];
  consentExpires: string | null;
}

export const listMyDisputes = (): Promise<{
  disputes: MarketplaceDispute[];
  note?: string;
}> => apiJson('/marketplace/me/disputes');
